import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import type React from "react";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { INK, STATUS, SURFACE } from "../../theme/tokens";
import { type SelfSchedulingDayStructure, SelfSchedulingPreference } from "../../utils/models";

type Props = { dayData: SelfSchedulingDayStructure; onClick: (date: DateTime, updatedPreference: SelfSchedulingPreference) => void };

const getCircleColor = (dayData: SelfSchedulingDayStructure) => {
	switch (dayData.dayPreference) {
		case SelfSchedulingPreference.RDO:
			return alpha(STATUS.ERROR.main, 0.6);
		case SelfSchedulingPreference.WANT_TO_WORK:
			return alpha(STATUS.SUCCESS.main, 0.8);
		case SelfSchedulingPreference.PREFER_NOT_TO_WORK:
		case SelfSchedulingPreference.WILLING_TO_WORK:
		case SelfSchedulingPreference.UNDEFINED:
			return SURFACE;
	}
};

const getTextColor = (dayData: SelfSchedulingDayStructure) => {
	switch (dayData.dayPreference) {
		case SelfSchedulingPreference.RDO:
			return SURFACE;
		case SelfSchedulingPreference.WANT_TO_WORK:
			return SURFACE;
		case SelfSchedulingPreference.PREFER_NOT_TO_WORK:
		case SelfSchedulingPreference.WILLING_TO_WORK:
		case SelfSchedulingPreference.UNDEFINED:
			return INK.SECONDARY;
	}
};

const iconSize = 10;

const getSubIcon = (dayData: SelfSchedulingDayStructure) => {
	switch (dayData.dayPreference) {
		case SelfSchedulingPreference.RDO:
			return null;
		case SelfSchedulingPreference.WANT_TO_WORK:
			return null;
		case SelfSchedulingPreference.PREFER_NOT_TO_WORK:
			return <ThumbDownIcon style={{ fontSize: iconSize }} />;
		case SelfSchedulingPreference.WILLING_TO_WORK:
			return <ThumbUpIcon style={{ fontSize: iconSize }} />;
		case SelfSchedulingPreference.UNDEFINED:
			return null;
	}
};

const SubText = ({ dayData, translate }: { dayData: SelfSchedulingDayStructure; translate: TFunction<"translation", undefined> }) => {
	const text =
		dayData.dayPreference === SelfSchedulingPreference.RDO
			? translate("rdo")
			: dayData.dayPreference === SelfSchedulingPreference.WANT_TO_WORK
				? translate("signed.up")
				: null;

	return text ? (
		<Typography
			style={{
				fontWeight: 600,
				fontSize: "9px",
				lineHeight: "100%",
				textAlign: "center",
				color: alpha(INK.SECONDARY, 0.5),
				userSelect: "none",
			}}
			sx={{
				mt: "3px",
				marginInline: 1,
			}}
		>
			{text}
		</Typography>
	) : null;
};

const enumLength = Object.keys(SelfSchedulingPreference).filter((k) => Number.isNaN(Number(k))).length;

const getUpdatedPreference = (dayData: SelfSchedulingDayStructure) => {
	return ((dayData.dayPreference + 1) % enumLength) as SelfSchedulingPreference;
};

/**
 * SelfSchedulingDay component displays a single day in the self-scheduling calendar.
 * @param dayData - The data for the day, including the date and current preference.
 * @param onClick - Optional callback function to handle clicks on the day.
 */
const SelfSchedulingDay: FC<Props> = ({ dayData, onClick }) => {
	const { t } = useTranslation();
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			event.stopPropagation();
			event.preventDefault();
			const updatedPreference = getUpdatedPreference(dayData);
			onClick(dayData.date, updatedPreference);
		},
		[dayData, onClick],
	);

	return (
		<Box
			onClick={handleClick}
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "start",
				cursor: "pointer",
			}}
		>
			<Box
				style={{
					width: "30px",
					height: "30px",
					backgroundColor: getCircleColor(dayData),
					borderRadius: "50%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
				sx={{
					mt: 1,
				}}
			>
				<Typography
					style={{
						fontWeight: 600,
						fontSize: "14px",
						lineHeight: "100%",
						textAlign: "center",
						color: getTextColor(dayData),
						userSelect: "none",
					}}
				>
					{dayData.date.day}
				</Typography>
			</Box>
			<SubText dayData={dayData} translate={t} />
			{getSubIcon(dayData)}
		</Box>
	);
};

export default SelfSchedulingDay;
